import React, { useMemo } from 'react';
import { useSpritePreview } from '@/hooks/useSpritePreview';
import {
    clampTooltipPosition,
    PROGRESS_THUMBNAIL_HEIGHT,
    PROGRESS_THUMBNAIL_WIDTH,
    PROGRESS_THUMBNAIL_PADDING,
} from '@/utils/video-player';
import { formatVideoTime } from '@/utils/time';

interface VideoProgressBarThumbnailProps {
    hoveredTime: number;
    mouseX: number;
    progressBarWidth: number;
    sourceId: string;
}

export const VideoProgressBarThumbnail: React.FC<
    VideoProgressBarThumbnailProps
> = ({ hoveredTime, mouseX, progressBarWidth, sourceId }) => {
    const { isLoading, isReady, getSprite } = useSpritePreview({ sourceId });

    const spriteInfo = useMemo(() => {
        if (!isReady) return null;
        return getSprite(hoveredTime);
    }, [isReady, getSprite, hoveredTime]);

    const leftPosition = clampTooltipPosition(
        mouseX,
        PROGRESS_THUMBNAIL_WIDTH,
        progressBarWidth,
        PROGRESS_THUMBNAIL_PADDING,
    );

    return (
        <div
            className="absolute bottom-full mb-[-8px] pointer-events-none"
            style={{
                left: `${leftPosition}px`,
                width: `${PROGRESS_THUMBNAIL_WIDTH}px`,
            }}
        >
            <div className="bg-grey-800 rounded-lg overflow-hidden shadow-lg">
                <div
                    className="relative bg-black flex items-center justify-center"
                    style={{
                        height: `${PROGRESS_THUMBNAIL_HEIGHT}px`,
                        width: `${PROGRESS_THUMBNAIL_WIDTH}px`,
                    }}
                >
                    {isLoading && !spriteInfo && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                    )}
                    {spriteInfo && (
                        <div
                            className="w-full h-full"
                            style={{
                                backgroundImage: `url(${spriteInfo.url})`,
                                backgroundPosition: `-${spriteInfo.x}px -${spriteInfo.y}px`,
                                backgroundSize: 'auto',
                                backgroundRepeat: 'no-repeat',
                            }}
                        />
                    )}
                    {!isLoading && !spriteInfo && (
                        <div className="text-grey-400 text-xs">
                            Preview unavailable
                        </div>
                    )}
                </div>
            </div>
            <span className="block text-center text-white-100 text-xs font-geist-mono mt-1 [text-shadow:0_0_1px_rgba(0,0,0,0.2)]">
                {formatVideoTime(hoveredTime)}
            </span>
        </div>
    );
};
